import React from "react";
import { Box, Stack, Tooltip, Typography, Chip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";

interface Counts {
  id: number;
  totalCases: number;
  passed: number;
  failed: number;
  interrupted: number;
  internal_error: number;
  no_tests: number;
  reserved: number;
  init: number;
}

const STATUSES: {
  key: keyof Counts;
  label: string;
  color: string;
  route: string;
}[] = [
  { key: "passed", label: "Passed", color: "#16a34a", route: "passed" },
  { key: "failed", label: "Failed", color: "#dc2626", route: "failed" },
  {
    key: "interrupted",
    label: "Interrupted",
    color: "#d97706",
    route: "interrupted",
  },
  { key: "internal_error", label: "Error", color: "#b91c1c", route: "internal_error" },
  { key: "no_tests", label: "No tests", color: "#94a3b8", route: "no_tests" },
  { key: "reserved", label: "Reserved", color: "#64748b", route: "reserved" },
  { key: "init", label: "Init", color: "#a3aab6", route: "init" },
];

const SuiteCasesBreakdown: React.FC<{ row: Counts }> = ({ row }) => {
  const navigate = useNavigate();
  const total = row.totalCases || 0;
  const passedPct = total > 0 ? Math.round((row.passed / total) * 100) : 0;

  const go = (path: string) => navigate(path);

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3, bgcolor: "background.level1" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
          Case results
          <Box
            component="span"
            sx={{ ml: 1, color: "text.secondary", fontWeight: 600 }}
          >
            {total} {total === 1 ? "case" : "cases"}
          </Box>
        </Typography>
        {total > 0 && (
          <Chip
            size="small"
            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            label={`${passedPct}% passed`}
            sx={{
              fontWeight: 700,
              bgcolor: passedPct === 100 ? "#e7f6ec" : "background.paper",
              color: passedPct === 100 ? "#16a34a" : "text.primary",
              border: "1px solid",
              borderColor: passedPct === 100 ? "#bfe6cd" : "divider",
            }}
          />
        )}
      </Stack>

      {/* Stacked distribution bar */}
      <Box
        sx={{
          display: "flex",
          height: 12,
          borderRadius: 6,
          overflow: "hidden",
          bgcolor: "#E4E7EC",
          mb: 2,
        }}
      >
        {total > 0 &&
          STATUSES.map((s) => {
            const count = (row[s.key] as number) || 0;
            if (count <= 0) return null;
            return (
              <Tooltip
                key={s.key}
                title={`${s.label}: ${count}`}
                arrow
                placement="top"
              >
                <Box
                  onClick={() => go(`/cases/${row.id}/${s.route}`)}
                  sx={{
                    width: `${(count / total) * 100}%`,
                    bgcolor: s.color,
                    cursor: "pointer",
                    transition: "filter 120ms ease",
                    "&:hover": { filter: "brightness(1.1)" },
                  }}
                />
              </Tooltip>
            );
          })}
      </Box>

      {/* Legend */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {STATUSES.map((s) => {
          const count = (row[s.key] as number) || 0;
          const active = count > 0;
          return (
            <Box
              key={s.key}
              onClick={active ? () => go(`/cases/${row.id}/${s.route}`) : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.25,
                py: 0.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: active ? "divider" : "transparent",
                bgcolor: active ? "background.paper" : "transparent",
                cursor: active ? "pointer" : "default",
                opacity: active ? 1 : 0.45,
                transition: "border-color 120ms ease, box-shadow 120ms ease",
                "&:hover": active
                  ? { borderColor: s.color, boxShadow: `0 0 0 1px ${s.color}33` }
                  : {},
              }}
            >
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  bgcolor: s.color,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                {s.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 800,
                  color: active ? "text.primary" : "text.secondary",
                }}
              >
                {count}
              </Typography>
            </Box>
          );
        })}

        {/* All cases */}
        <Box
          onClick={total > 0 ? () => go(`/cases/${row.id}`) : undefined}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "primary.main",
            color: "primary.main",
            cursor: total > 0 ? "pointer" : "default",
            opacity: total > 0 ? 1 : 0.45,
            "&:hover": total > 0 ? { bgcolor: "primary.lighter" } : {},
          }}
        >
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 800 }}>
            All cases
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 800 }}>
            {total}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SuiteCasesBreakdown;
